import _ from 'underscore';
import React, { useCallback, useEffect, useState, useContext, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  DefaultButton,
  Label,
  PrimaryButton,
  Stack,
  getId,
} from '@fluentui/react';
import { FluentProvider, makeStyles, webLightTheme } from '@fluentui/react-components';
import { HelpPopupInline } from '@bingads-webui-react/help-popup';
import {
  AssetEventFeature,
  AssetPropertyName,
  getCopilotRecommendationIdsFromThemes,
  getFormattedThemeForApplying,
  getUniqueAssetsFromThemes,
  useCopilotRecommendationSurvey,
  useAssetLibraryEventHandler,
  getFormattedImage,
  logCopilotRecommendations,
} from '@bingads-webui-campaign-react/copilot-common';
import { ResponsiveSearchAdPreviewUAPL } from '@bingads-webui-campaign-react/responsive-search-ad-preview-uapl';
import { ResponsiveSearchAdStrength } from '@bingads-webui-campaign-react/responsive-search-ad-suggestion';
import {
  UrlOptionsParameters,
  validateTrackingUrlTemplate,
  validateFinalUrlSuffix,
  validateUrlCustomParameters,
} from '@bingads-webui-campaign-react/url-options';
import { PermissionsContext } from '@bingads-webui-react/hoc-with-permissions';
import { initializeAdCustomizer } from '@bingads-webui-campaign/ad-customizers-next';
import { HeadlineAssetCollection, DescriptionAssetCollection, useCopilotRecommendations, RecommendationContext, withRecommendationContext, formatToLLMSuggestion, MAX_INLINE_RECOMMENDATIONS_PER_PAGE } from '@bingads-webui-campaign/responsive-search-ad-recommendation';
import { InstrumentationContext } from '@bingads-webui-react/hoc-with-instrumentation';
import {
  getUrlPrefixAndDestination,
  getUpdatedFullUrlOnPrefixUpdate,
  getUpdatedFullUrlOnTextUpdate,
  validateAdUrl
} from '@bingads-webui-campaign-react/url-input-fluent';
import { Collapse, TextTitle } from '@bingads-webui-campaign/wizard-jumbo-card';
import { setClarityTag } from '@bingads-webui-universal/clarity';
import { CopilotRepositoryProvider, withBannerService } from '@bingads-webui/inline-copilot';
import { withActivityContext } from '@bingads-webui-react/hoc-with-signal';

import {
  adsFields,
  adsFieldAlias,
  adsFieldLengthLimitMap,
  validateTextFieldValue,
  validateTextAssets,
  validatePath,
  AD_TYPE_RSA,
} from './validators';
import { containerStyles, contentStyles, stickyAdPreviewStyles } from './styles';
import { ErrorContext } from './controls/error';
import { UrlInput } from './controls/url-input';
import { UrlPath } from './controls/url-path';
import { formatRankingTexts, computeRankingTextsUpdates } from './utils/format';
import { TextAssets } from './controls/text-assets';
import { Disclaimer } from './controls/disclaimer';
import { adFieldsConfig, headlinesConfig, descriptionsConfig } from './config';
import { useCopilotForResponsiveSearchAd } from './use-copilot-for-responsive-search-ad';
import { ResponsiveSearchAdCopilotBannerWithDialog } from './responsive-search-ad-copilot-banner-with-dialog';
import UrlIcon from './media/url.svg';
import TextIcon from './media/text.svg';
import ImageIcon from './media/image.svg';
import DisclaimerIcon from './media/disclaimer.svg';

// keep the order to ensure the focus order when errors are present
const adsFieldsToValidate = [
  adsFields.DESTINATION_URL,
  adsFields.MOBILE_URL,
  adsFields.PATH1,
  adsFields.PATH2,
  adsFields.HEADLINES,
  adsFields.DESCRIPTIONS,
  adsFields.TRACKING_URL_TEMPLATE,
  adsFields.FINAL_URL_SUFFIX,
  adsFields.URL_CUSTOM_PARAMETERS,
];

const optionalFields = [
  adsFields.HEADLINES,
  adsFields.DESCRIPTIONS,
  adsFields.PATH1,
  adsFields.PATH2,
  adsFields.MOBILE_URL,
  adsFields.TRACKING_URL_TEMPLATE,
  adsFields.FINAL_URL_SUFFIX,
  adsFields.URL_CUSTOM_PARAMETERS,
];

const requiredCompoundFields = [
  adsFields.HEADLINES,
  adsFields.DESCRIPTIONS,
];

const textAssets = [
  adsFields.HEADLINES,
  adsFields.DESCRIPTIONS,
];

const isSingleFieldOptional = field => _.contains(optionalFields, field);
const isCompoundFieldRequired = field => _.contains(requiredCompoundFields, field);

const isFieldEmpty = (field, fieldValue) => {
  switch (field) {
    case adsFields.HEADLINES:
    case adsFields.DESCRIPTIONS:
      return _.every(fieldValue, value => _.isEmpty(_.property(['Asset', 'Text'])(value)));
    default:
      break;
  }
  return _.isEmpty(fieldValue);
};

const fieldMappings = {};
_.each(_.range(15), (i) => { fieldMappings[`Headline${i}`] = 'Headlines'; });
_.each(_.range(4), (i) => { fieldMappings[`Description${i}`] = 'Descriptions'; });

const fieldDependencies = {
  ...fieldMappings,
  [adsFields.PATH1]: adsFields.PATH2,
  [adsFields.PATH2]: adsFields.PATH1,
};

const validators = {
  [adsFields.PATH1]: validatePath,
  [adsFields.PATH2]: validatePath,
  [adsFields.TRACKING_URL_TEMPLATE]: validateTrackingUrlTemplate,
  [adsFields.FINAL_URL_SUFFIX]: validateFinalUrlSuffix,
  [adsFields.URL_CUSTOM_PARAMETERS]: validateUrlCustomParameters,
};

const useStyles = makeStyles({
  theme: {
    backgroundColor: 'transparent',
  },
});

export function ResponsiveSearchAdEditorBase(props) {
  const {
    item,
    errors,
    i18n,
    onSave,
    onCancel,
    onChanged,
    onFetchAdStrengthV2,
    onFetchDisclaimerInfo,
    showActionBar,
    showInitialErrors,
    timeZone,
    AdPreviewComponent,
    permissions,
    token,
    triggerTestUurl,
    showUurlTestTool,
    showFinalUrlSuffix,
    showAdCustomizers,
    showDynamicTextExtention,
    isForVnext,
    updateSuggestionMeasurement,
    edmCampaign,
    jsDataCampaign,
    renderImageExtension,
    injectSetPreviewImage,
    perfMarker,
    serverContext,
    scope,
    editMode,
    saveLogAppliedSuggestionsFunc,
    saveAssetLibraryEventHandler,
    isSaving,
    isCanceling,
    onFirstErrorHandler,
    isNewTextAssetsButtonStyle,
    activityLogger,
  } = props;
  const instrumentation = useContext(InstrumentationContext);

  const styles = useStyles();

  initializeAdCustomizer({
    deps: {
      i18n,
      permissions,
      instrumentation,
      timeZone,
      showAdCustomizers,
      edmCampaign,
      jsDataCampaign,
      scope,
    },
    adType: AD_TYPE_RSA,
  });

  if (permissions.IsVanityUrlEnabled && !_.contains(adsFieldsToValidate, adsFields.DISPLAY_URL)) {
    adsFieldsToValidate.push(adsFields.DISPLAY_URL);
  }
  const [cachedAd, setCachedAd] = useState(item);
  const [cachedValuesForImageSuggestions, setCachedValuesForImageSuggestions] = useState(() =>
    formatRankingTexts({
      Headlines: item.data.Headlines,
      Descriptions: item.data.Descriptions,
    }));

  const {
    data,
    errors: clientErrors,
  } = cachedAd;

  const {
    Headlines,
    Descriptions,
    Path1,
    Path2,
    FinalUrls,
    DisplayUrl,
    MobileFinalUrls,
    TrackingUrlTemplate,
    FinalUrlSuffix,
    UrlCustomParameters,
    Id,
  } = data;

  const [formErrors, setFormErrors] = useState(clientErrors);
  const errorContextValue = useMemo(() => ({ errors: formErrors, onFirstErrorHandler }), [formErrors, onFirstErrorHandler]);
  const hasErrorInUrl = useMemo(() => _.some(formErrors, ({
    Property,
  }) => Property === adFieldsConfig.DestinationUrl.key), [formErrors]);

  const finalUrl = useMemo(() => _.first(FinalUrls) || '', [FinalUrls]);
  const [finalUrlPrefix, finalUrlWithoutPrefix] = useMemo(() => getUrlPrefixAndDestination(finalUrl), [finalUrl]);

  const finalUrlForRecommendation = _.isEmpty(finalUrlWithoutPrefix) || hasErrorInUrl ? '' : finalUrl;

  const urlField = getId(adsFields.DESTINATION_URL);
  const mobileUrlField = getId(adsFields.MOBILE_URL);
  const pathField = getId(adsFields.PATH1);
  const displayUrlField = getId(adsFields.DISPLAY_URL);

  const [isInExclusionList, setIsInExclusionList] = useState(true);
  const [disclaimerInfo, setDisclaimerInfo] = useState(null);
  const [hasDisclaimer, setHasDisclaimer] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [llmAssetsSuggestions, setLLMAssetsSuggestions] = useState({});
  const [activeKeys, setActiveKeys] = useState([
    'url',
    'headlines',
    'descriptions',
    'imageExtensions',
    'disclaimer',
  ]);

  const existingAssets = useMemo(() => ({
    shortHeadlines: Object.values(_.result(data, 'Headlines', {})).map(singleItem => singleItem.Asset.Text),
    descriptions: Object.values(_.result(data, 'Descriptions', {})).map(singleItem => singleItem.Asset.Text),
  }), [data]);

  const campaignId = scope && scope.campaignId;
  const adGroupId = scope && scope.adGroupId;

  const [recommendationContext, setRecommendationContext] = useContext(RecommendationContext);

  const { logRecommendationsAPIError, resetRecommendations } = useCopilotRecommendations({
    campaignId,
    serverContext,
    editMode,
    entityId: Id,
    instrumentation,
    activityLogger,
  });

  const {
    copilotTracking, themes, postAssetEventLog = _.noop,
  } = recommendationContext;

  const {
    eventLog,
    isCopilotEnabled,
    sendCopilotBannerNotification,
  } = useCopilotForResponsiveSearchAd({ permissions, postAssetEventLog, serverContext });
  const assetLibraryEventHandler = useAssetLibraryEventHandler(postAssetEventLog);

  const {
    logEnterFlow,
    logBannerGenerated,
    logFetchAssetsSucceeded,
    logApplyThemes,
    logSuggestionsViewed,
    logSuggestionsApplied,
    logRefineTextAssetSucceeded,
    logPrefillAssets,
    logPrefillAssetsApplied,
    logPrefilledAdStrength,
    logRemoveAssets,
  } = eventLog;

  const {
    triggerSurveyOnSubmit,
    triggerSurveyOnCancel,
  } = useCopilotRecommendationSurvey(AssetEventFeature.RSA);

  useEffect(() => {
    if (isSaving) {
      triggerSurveyOnSubmit();
    }
  }, [isSaving, triggerSurveyOnSubmit]);

  useEffect(() => {
    if (isCanceling && _.size(themes) > 0) {
      triggerSurveyOnCancel();
    }
  }, [isCanceling, triggerSurveyOnCancel, themes]);

  useEffect(() => {
    logEnterFlow();
  }, [logEnterFlow]);

  const [isPrefilled, setIsPrefilled] = useState(false);
  const [shouldLogPrefilledAdStrength, setShouldLogPrefilledAdStrength] = useState(false);

  const [applyHeadlinesCallback, setApplyHeadlinesCallback] = useState(_.noop);
  const [applyDescriptionsCallback, setApplyDescriptionsCallback] = useState(_.noop);

  const logPrefilledAdStrengthCallback = useCallback((adStrength) => {
    // Only log the ad strength once after prefilling
    if (shouldLogPrefilledAdStrength) {
      logPrefilledAdStrength(adStrength, copilotTracking.initialSuggestionsData);
      setShouldLogPrefilledAdStrength(false);
    }
  }, [shouldLogPrefilledAdStrength, logPrefilledAdStrength, copilotTracking.initialSuggestionsData]);

  useEffect(() => {
    if (themes && themes.length > 0 && copilotTracking) {
      const {
        descriptionRecommendationIds, headlineRecommendationIds,
      } = getCopilotRecommendationIdsFromThemes(themes);

      if (permissions && !permissions.dynamic.AdStrengthTestingNoPrefill && !isInExclusionList && cachedAd.data && !cachedAd.data.Headlines && !cachedAd.data.Descriptions) {
        // text assets are empty, prefill with copilot recommendations
        setIsPrefilled(true);

        sendCopilotBannerNotification('Apply');
        if (applyHeadlinesCallback) {
          // prefill with the first 12 headlines
          let newThems = themes;
          if (permissions && permissions.dynamic && permissions.dynamic.CopilotDKIRecommendation) {
            // prefill with the max 2 DKI headlines
            const DKITheme = _.first(themes.filter(theme => theme.name === 'Asset_Theme_For_DKI'));
            const normalThemes = themes.filter(theme => theme.name !== 'Asset_Theme_For_DKI');
            if (DKITheme && DKITheme.headlines) {
              newThems = [{ ...DKITheme, headlines: DKITheme.headlines.slice(0, 2) }, ...normalThemes];
            }
          }
          const newHeadlines = getUniqueAssetsFromThemes(newThems, 'headlines', 12);
          copilotTracking.trackInitialSuggestions(AssetPropertyName.HEADLINE, newHeadlines);

          applyHeadlinesCallback(newHeadlines);
        }
        if (applyDescriptionsCallback) {
          // prefill with the first 3 descriptions
          const newDescriptions = getUniqueAssetsFromThemes(themes, 'descriptions', 3);
          copilotTracking.trackInitialSuggestions(AssetPropertyName.DESCRIPTION, newDescriptions);

          applyDescriptionsCallback(newDescriptions);
        }
        setShouldLogPrefilledAdStrength(true);
        sendCopilotBannerNotification('ApplySucceeded');
        logPrefillAssets();
      }

      // By default, user views first 5 copilot text recommendations when they are returned
      const displayedDescriptions = _.first(descriptionRecommendationIds, MAX_INLINE_RECOMMENDATIONS_PER_PAGE);
      const displayedHeadlines = _.first(headlineRecommendationIds, MAX_INLINE_RECOMMENDATIONS_PER_PAGE);
      const displayedTextAssets = _.flatten([displayedDescriptions, displayedHeadlines]);
      const viewedTextSuggestionIds = copilotTracking.getUntrackedViewedSuggestionIds(displayedTextAssets);
      if (viewedTextSuggestionIds.length > 0) {
        logSuggestionsViewed(viewedTextSuggestionIds);
        copilotTracking.trackViewedSuggestionIds(viewedTextSuggestionIds);
      }
    }
  }, [
    applyDescriptionsCallback,
    applyHeadlinesCallback,
    isInExclusionList,
    cachedAd.data,
    copilotTracking,
    logPrefillAssets,
    logSuggestionsViewed,
    sendCopilotBannerNotification,
    themes,
    permissions,
  ]);

  useEffect(() => {
    if (saveAssetLibraryEventHandler) {
      saveAssetLibraryEventHandler(assetLibraryEventHandler);
    }
  }, [saveAssetLibraryEventHandler, assetLibraryEventHandler]);


  useEffect(() => {
    if (saveLogAppliedSuggestionsFunc) {
      saveLogAppliedSuggestionsFunc(({
        headlines,
        descriptions,
        entityId,
        suggestedAdExtensions = [],
      }) => {
        logCopilotRecommendations({
          recommendationContext,
          assetGroup: {
            Headlines: headlines,
            Descriptions: descriptions,
            Images: {
              ImageList: suggestedAdExtensions.map(ext => ({ id: ext.Images && ext.Images[0] && ext.Images[0].Asset.Id })),
            },
            Id: entityId,
          },
          campaignId,
          isEditing: editMode,
        });
      });
    }
  }, [saveLogAppliedSuggestionsFunc, logSuggestionsApplied, logPrefillAssetsApplied, recommendationContext, editMode, campaignId, copilotTracking, isPrefilled]);

  const handleCopilotBannerFetchSucceeded = useCallback((url, assetsResponse) => {
    const { IsCustomerIdInExclusionList } = assetsResponse;
    if (IsCustomerIdInExclusionList !== isInExclusionList) {
      setIsInExclusionList(IsCustomerIdInExclusionList);
    }
    logBannerGenerated(url, assetsResponse);
    if (copilotTracking) {
      copilotTracking.trackReturnedTextAssets(assetsResponse.RecommendedAssets);
    }

    const formattedThemesAndAssets = _.map(assetsResponse.RecommendedAssets.Themes, getFormattedThemeForApplying);
    setRecommendationContext({ themes: formattedThemesAndAssets, imageAssets: _.map(assetsResponse.RecommendedAssets.ImageAssets, getFormattedImage) });
  }, [copilotTracking, isInExclusionList, logBannerGenerated, setRecommendationContext]);

  const onApplyCopilotRecommendation = useCallback((selectedThemes, selectedThemesAndAssets) => {
    logApplyThemes(selectedThemes);

    const formattedThemesAndAssets = _.map(selectedThemesAndAssets, getFormattedThemeForApplying);

    sendCopilotBannerNotification('Apply');
    setRecommendationContext({ themes: [...formattedThemesAndAssets] });

    if (applyHeadlinesCallback) {
      applyHeadlinesCallback(getUniqueAssetsFromThemes(formattedThemesAndAssets, 'headlines'));
    }

    if (applyDescriptionsCallback) {
      applyDescriptionsCallback(getUniqueAssetsFromThemes(formattedThemesAndAssets, 'descriptions'));
    }

    sendCopilotBannerNotification('ApplySucceeded');
  }, [sendCopilotBannerNotification, setRecommendationContext, applyHeadlinesCallback, applyDescriptionsCallback, logApplyThemes]);

  const handleFetchAssetsSucceeded = useCallback((url, assetsRecommendationData) => {
    logFetchAssetsSucceeded(url, assetsRecommendationData);
    if (copilotTracking) {
      copilotTracking.trackReturnedTextAssets(assetsRecommendationData.RecommendedAssets);
    }
  }, [logFetchAssetsSucceeded, copilotTracking]);

  const handleTextAssetSuggestionsRendered = useCallback((url, params, textSuggestions) => {
    const textSuggestionIds = _.pluck(textSuggestions, 'RecommendationId');
    logRefineTextAssetSucceeded(url, params, textSuggestionIds);

    if (copilotTracking) {
      copilotTracking.trackReturnedTextAssets({ TextAssets: textSuggestions });
      copilotTracking.trackViewedSuggestionIds(textSuggestionIds);
    }
  }, [copilotTracking, logRefineTextAssetSucceeded]);

  const formatRecommendationsToLLMSuggestions = useCallback(() => {
    if (!recommendationContext.themes || recommendationContext.themes.length === 0) return {};
    return formatToLLMSuggestion(recommendationContext, data);
  }, [data, recommendationContext]);

  useEffect(() => {
    injectSetPreviewImage(setPreviewImage);
  }, [injectSetPreviewImage]);

  const onValidate = useCallback((value, activeField = null) => {
    const shouldValidateAllFields = activeField === null;

    let newErrors;
    let errorFields;
    if (shouldValidateAllFields) {
      // validating all fields, start with an empty list
      errorFields = [];
      newErrors = [];
    } else {
      // validating activeField only, remove any pre-existing error for activeField only
      errorFields = _.filter(value.errorFields, f => f !== activeField);
      newErrors = _.filter(value.errors, e => e.Property !== activeField);
    }
    // also validate dependant fields
    const dependantField = fieldDependencies[activeField];
    if (dependantField) {
      errorFields = _.without(errorFields, dependantField);
      newErrors = _.filter(newErrors, e => e.Property !== dependantField);

      // also validate fields with alias, currently only dependant fields have aliases
      const aliasField = adsFieldAlias[dependantField];
      if (aliasField) {
        errorFields = _.without(errorFields, aliasField);
        newErrors = _.filter(newErrors, e => e.Property !== aliasField);
      }
    }

    let incomplete = false;

    _.each(adsFieldsToValidate, (adField) => {
      const isOptional = isSingleFieldOptional(adField);

      if (shouldValidateAllFields || adField === (dependantField || activeField)) {
        let fieldValue = value.data[adField];
        const maxLength = adsFieldLengthLimitMap[adField];

        let hasError;
        switch (adField) {
          case adsFields.DESTINATION_URL:
          case adsFields.MOBILE_URL: {
            // validate URL
            const url = _.first(fieldValue);
            const [, destination] = getUrlPrefixAndDestination(url);

            if (isOptional && !destination) {
              hasError = false;
            } else {
              const error = validateAdUrl(url, '', i18n);
              if (!_.isEmpty(error)) {
                newErrors.push({
                  Property: adField,
                  Message: error,
                });
              }
              hasError = !fieldValue || error;
            }
            break;
          }
          case adsFields.DISPLAY_URL: {
            if (permissions.IsVanityUrlEnabled) {
              const displayUrlError =
                validateTextFieldValue(fieldValue, maxLength, i18n, adField, isOptional);
              hasError = displayUrlError !== null;
              if (hasError) {
                newErrors.push({
                  Property: adField,
                  Message: displayUrlError,
                });
                errorFields.push(adField);
              }
            }
            break;
          }
          case adsFields.HEADLINES:
          case adsFields.DESCRIPTIONS: {
            hasError = _.any(
              fieldValue,
              h => validateTextFieldValue(h.Asset.Text, maxLength, i18n, adField, isOptional)
            );

            // eslint-disable-next-line max-len
            const error = validateTextAssets(fieldValue, adFieldsConfig[adField].assetsConfig, i18n);

            if (!_.isEmpty(error)) {
              hasError = true;
              newErrors.push({
                Property: adField,
                Message: error,
              });
            }
            break;
          }
          case adsFields.PATH1:
          case adsFields.PATH2: {
            if (shouldValidateAllFields && adField === adsFields.PATH2) {
              break;
            }

            fieldValue = [value.data.Path1, value.data.Path2];
            // fallthrough
          }
          // eslint-disable-next-line no-fallthrough
          case adsFields.TRACKING_URL_TEMPLATE:
          case adsFields.FINAL_URL_SUFFIX:
          case adsFields.URL_CUSTOM_PARAMETERS: {
            const errorList = validators[adField](fieldValue, i18n);
            if (!_.isEmpty(errorList)) {
              hasError = true;
              newErrors.push(...errorList);
            }
            break;
          }
          default: {
            hasError = validateTextFieldValue(fieldValue, maxLength, i18n, adField, isOptional);
          }
        }

        if (hasError) {
          errorFields.push(adField);
        }
      } else if ((!isOptional || isCompoundFieldRequired(adField)) && isFieldEmpty(adField, value.data[adField])) {
        // if not validating all fields, still need to check if ad is incomplete
        incomplete = true;
      }
    });

    // if there are any errors, ad is not complete yet
    if (!_.isEmpty(errorFields)) {
      incomplete = true;
      errorFields = _.uniq(errorFields);
    }

    setFormErrors(newErrors);

    return {
      ...value,
      errorFields,
      errors: newErrors,
      incomplete,
    };
  }, [i18n, permissions.IsVanityUrlEnabled]);

  const onClearTextAssets = useCallback(() => {
    const validatedItem = onValidate({
      ...cachedAd,
      data: {
        ...data,
        ...textAssets.reduce((acc, key) => {
          acc[key] = [];
          return acc;
        }, {}),
      },
    });
    setCachedAd(validatedItem);

    copilotTracking.removeAllInitialSuggestions();
    logRemoveAssets();
  }, [cachedAd, data, onValidate, logRemoveAssets, copilotTracking]);

  const onUpdateItemByKey = useCallback((value, key) => {
    setCachedAd((prevCachedAd) => {
      const updatedData = {
        ...prevCachedAd.data,
        [fieldMappings[key] || key]: value,
      };

      const validatedItem = onValidate({
        ...prevCachedAd,
        data: updatedData,
      }, key);

      return validatedItem;
    });
  }, [onValidate]);

  const onDebouncedUpdateItemByKey = useCallback(_.debounce((value, key) => {
    setCachedAd((prevCachedAd) => {
      const updatedData = {
        ...prevCachedAd.data,
        [fieldMappings[key] || key]: value,
      };

      const validatedItem = onValidate({
        ...prevCachedAd,
        data: updatedData,
      }, key);

      return validatedItem;
    });
  }, 1000), [onValidate]);

  const onItemBlurBykey = useCallback((newValue, key) => {
    if (renderImageExtension) {
      const fieldToUpdate = fieldMappings[key] || key;

      if (_.contains([
        adsFields.HEADLINES, adsFields.DESCRIPTIONS,
      ], fieldToUpdate)) {
        setCachedValuesForImageSuggestions((prev) => {
          const { needUpdate, value } = computeRankingTextsUpdates({
            Headlines,
            Descriptions,
            [fieldToUpdate]: newValue,
          }, prev);

          if (needUpdate) {
            return value;
          }
          return prev;
        });
      }
    }
  }, [Descriptions, Headlines, renderImageExtension]);

  const onUpdateFinalUrlPrefix = useCallback((evt, newPrefix) => {
    const newValue = getUpdatedFullUrlOnPrefixUpdate(finalUrlWithoutPrefix, newPrefix);

    onUpdateItemByKey([newValue], adFieldsConfig.DestinationUrl.key);
  }, [finalUrlWithoutPrefix, onUpdateItemByKey]);

  const onUpdateFinalUrlValue = useCallback((evt) => {
    const newValue = getUpdatedFullUrlOnTextUpdate(finalUrlPrefix, evt.target.value);

    if (newValue) {
      onUpdateItemByKey([newValue], adFieldsConfig.DestinationUrl.key);
    }
  }, [finalUrlPrefix, onUpdateItemByKey]);

  const onSaveClicked = useCallback(() => {
    const validatedItem = onValidate(cachedAd);

    setCachedAd(validatedItem);
    if (!validatedItem.incomplete && _.isEmpty(validatedItem.errorFields)) {
      onSave(validatedItem);
    }
  }, [cachedAd, onSave, onValidate]);

  const onCancelClicked = () => {
    onCancel();
  };

  useEffect(() => {
    onChanged(cachedAd);
  }, [cachedAd]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const validatedItem = onValidate(cachedAd);
    if (!showInitialErrors) {
      setCachedAd({
        ...validatedItem,
        errorFields: [],
        errors: [],
      });
    } else {
      setCachedAd(validatedItem);
    }

    if (!showInitialErrors) {
      setFormErrors([]);
    }
  }, [showInitialErrors]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setFormErrors(_.extend([], formErrors, errors));
  }, [errors]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!hasErrorInUrl) {
      const copilotSuggestions = formatRecommendationsToLLMSuggestions();
      setLLMAssetsSuggestions(copilotSuggestions);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalUrlForRecommendation, recommendationContext]);

  useEffect(() => {
    if (isForVnext && onFetchDisclaimerInfo) {
      onFetchDisclaimerInfo().then((disclaimer) => {
        const {
          disclaimerData,
        } = disclaimer;
        if (disclaimer.hasDisclaimer) {
          setDisclaimerInfo(disclaimerData);
          setHasDisclaimer(disclaimer.hasDisclaimer);
        }
      });
    }
  }, [isForVnext, onFetchDisclaimerInfo]);

  useEffect(() => {
    setClarityTag({ RSANewCopilotUI: 'true' });
  }, []);

  const renderLeftColumnWithNewCopilotUI = () => {
    const errorProperties = (formErrors || []).map(error => error.Property);
    const renderCardError = properties => (_.some(properties, property => _.contains(errorProperties, property)) ? _TL_("* Something's wrong") : null);
    const renderCardTitle = (name, icon, helpId) => <TextTitle titleKey={name} icon={icon} iconAltText={i18n.getString(name)} topicKey={helpId} as="h3" headingClassName="rsa-card-title" />;
    const imageExtension = renderImageExtension && renderImageExtension({
      fullUrl: finalUrl,
      hasErrorInUrl,
      urlPrefix: finalUrlPrefix,
      onUpdateUrlPrefix: onUpdateFinalUrlPrefix,
      onUpdateUrlValue: onUpdateFinalUrlValue,
      headlines: cachedValuesForImageSuggestions.Headlines,
      descriptions: cachedValuesForImageSuggestions.Descriptions,
    });

    return (
      <FluentProvider theme={webLightTheme} className={styles.theme}>
        <CopilotRepositoryProvider
          campaignId={campaignId}
          finalUrl={finalUrlForRecommendation}
          locale={i18n.getLocale()}
          recommendationType="RSA"
          perfMarker={perfMarker}
          serverContext={serverContext}
        >
          <Stack className="asset-group-assets-section rsa-left-copilot-column">
            <Collapse activeKeys={activeKeys} setActiveKeys={setActiveKeys}>
              <Collapse.Panel key="url" title={renderCardTitle(_TL_('URL'), UrlIcon)} error={renderCardError(['FinalUrls', 'MobileFinalUrls', 'DisplayUrl', 'Path1', 'Path2'])}>
                <Stack>
                  <Label htmlFor={urlField}>
                    <Stack horizontal verticalAlign="center" >
                      {i18n.getString(_TL_('Final URL'))}
                      <HelpPopupInline topic="pop_BA_NewAdCreate_FinalURL" ariaLabel={i18n.getString(_TL_('Click to learn more about Final URL'))} />
                    </Stack>
                  </Label>
                  <UrlInput
                    config={adFieldsConfig.DestinationUrl}
                    url={finalUrl}
                    onChange={onDebouncedUpdateItemByKey}
                    i18n={i18n}
                  />
                </Stack>
                <Stack>
                  <Label htmlFor={mobileUrlField}>
                    <Stack horizontal verticalAlign="center" >
                      {i18n.getString(_TL_('Mobile URL'))}
                      <HelpPopupInline topic="pop_BA_NewCampaignCreate_Ad_MobileURL" ariaLabel={i18n.getString(_TL_('Click to learn more about Mobile URL'))} />
                    </Stack>
                  </Label>
                  <UrlInput
                    config={adFieldsConfig.MobileUrl}
                    url={_.first(MobileFinalUrls) || ''}
                    onChange={onDebouncedUpdateItemByKey}
                    i18n={i18n}
                  />
                </Stack>
                <Stack>
                  {permissions.IsVanityUrlEnabled ?
                    <Label htmlFor={displayUrlField}>
                      <Stack horizontal verticalAlign="center" >
                        {i18n.getString(_TL_('Display URL'))}
                        <HelpPopupInline topic="pop_BA_NewAdCreate_VanityURL" ariaLabel={i18n.getString(_TL_('Click to learn more about Display URL'))} />
                      </Stack>
                    </Label> :
                    <Label htmlFor={pathField}>
                      <Stack horizontal verticalAlign="center" >
                        {i18n.getString(_TL_('Path'))}
                        <HelpPopupInline topic="pop_BA_NewAdCreate_Path" ariaLabel={i18n.getString(_TL_('Click to learn more about Path'))} />
                      </Stack>
                    </Label>
                  }
                  <UrlPath
                    config={adFieldsConfig}
                    value={[Path1, Path2]}
                    url={_.first(FinalUrls)}
                    onChange={onUpdateItemByKey}
                    DisplayUrl={DisplayUrl}
                    i18n={i18n}
                    IsVanityUrlEnabled={permissions.IsVanityUrlEnabled}
                  />
                </Stack>
              </Collapse.Panel>
            </Collapse>
            <ResponsiveSearchAdCopilotBannerWithDialog
              eventLog={eventLog}
              isCopilotEnabled={isCopilotEnabled}
              onApply={onApplyCopilotRecommendation}
              onDialogFetchAssetsSucceeded={handleFetchAssetsSucceeded}
              onFetch={resetRecommendations}
              onFetchFailed={logRecommendationsAPIError}
              onFetchSucceeded={handleCopilotBannerFetchSucceeded}
              onReset={onClearTextAssets}
            />
            <Collapse activeKeys={activeKeys} setActiveKeys={setActiveKeys}>
              <Collapse.Panel key="headlines" title={renderCardTitle(_TL_('Headlines'), TextIcon, 'pop_BA_RSA_Headlines')} error={renderCardError(['Headlines'])}>
                <Stack>
                  <TextAssets
                    isForVnext={isForVnext}
                    config={headlinesConfig}
                    assets={Headlines}
                    minSize={5}
                    maxSize={15}
                    finalUrlWithoutPrefix={finalUrlWithoutPrefix}
                    existingAssets={existingAssets}
                    i18n={i18n}
                    onChange={onUpdateItemByKey}
                    onBlur={onItemBlurBykey}
                    updateSuggestionMeasurement={updateSuggestionMeasurement}
                    showDynamicTextExtention={showDynamicTextExtention}
                    llmAssetsSuggestions={llmAssetsSuggestions}
                    permissions={permissions}
                    onSuggestionsRendered={handleTextAssetSuggestionsRendered}
                    showDeleteButton
                    showCopilotButton={isCopilotEnabled}
                    isNewTextAssetsButtonStyle={isNewTextAssetsButtonStyle}
                  />
                  <HeadlineAssetCollection
                    data={data}
                    onChange={onUpdateItemByKey}
                    updateSuggestionMeasurement={updateSuggestionMeasurement}
                    setApplyHeadlinesCallback={setApplyHeadlinesCallback}
                  />
                </Stack>
              </Collapse.Panel>
              <Collapse.Panel key="descriptions" title={renderCardTitle(_TL_('Descriptions'), TextIcon, 'pop_BA_RSA_Descriptions')} error={renderCardError(['Descriptions'])}>
                <Stack>
                  <TextAssets
                    isForVnext={isForVnext}
                    config={descriptionsConfig}
                    assets={Descriptions}
                    minSize={2}
                    maxSize={4}
                    finalUrlWithoutPrefix={finalUrlWithoutPrefix}
                    existingAssets={existingAssets}
                    i18n={i18n}
                    onChange={onUpdateItemByKey}
                    onBlur={onItemBlurBykey}
                    updateSuggestionMeasurement={updateSuggestionMeasurement}
                    showDynamicTextExtention={showDynamicTextExtention}
                    llmAssetsSuggestions={llmAssetsSuggestions}
                    permissions={permissions}
                    onSuggestionsRendered={handleTextAssetSuggestionsRendered}
                    showDeleteButton
                    showCopilotButton={isCopilotEnabled}
                    isNewTextAssetsButtonStyle={isNewTextAssetsButtonStyle}
                  />
                  <DescriptionAssetCollection
                    data={data}
                    onChange={onUpdateItemByKey}
                    updateSuggestionMeasurement={updateSuggestionMeasurement}
                    setApplyDescriptionsCallback={setApplyDescriptionsCallback}
                  />
                </Stack>
              </Collapse.Panel>
              {imageExtension &&
                <Collapse.Panel key="imageExtensions" title={renderCardTitle(_TL_('Image extensions'), ImageIcon, 'pop_BA_NewAdCreate_ImageExtensions')} >
                  {imageExtension}
                </Collapse.Panel>
              }
              {hasDisclaimer &&
                <Collapse.Panel key="disclaimer" title={renderCardTitle(_TL_('Disclaimer'), DisclaimerIcon, 'pop_BA_DisclaimerAd')} >
                  <Stack>
                    <Disclaimer disclaimerInfo={disclaimerInfo} i18n={i18n} permissions={permissions} />
                  </Stack>
                </Collapse.Panel>
              }
              <Collapse.Panel key="adUrlOptions" title={renderCardTitle(_TL_('Ad URL options (optional)'), UrlIcon)} error={renderCardError(['TrackingUrlTemplate', 'FinalUrlSuffix', 'UrlCustomParameters'])}>
                <UrlOptionsParameters
                  trackingUrlTemplate={TrackingUrlTemplate}
                  finalUrlSuffix={FinalUrlSuffix}
                  urlCustomParameters={UrlCustomParameters}
                  errors={formErrors}
                  onChange={onUpdateItemByKey}
                  i18n={i18n}
                  token={token}
                  triggerTestUurl={triggerTestUurl}
                  showUurlTestTool={showUurlTestTool}
                  showFinalUrlSuffix={showFinalUrlSuffix}
                  instrumentation={instrumentation}
                />
              </Collapse.Panel>
            </Collapse>
            {showActionBar &&
              <Stack
                horizontal
                id={getId('actionStack')}
                tokens={contentStyles.actionStackToken}
              >
                <PrimaryButton
                  className="rsa-editor-save-button"
                  onClick={onSaveClicked}
                  disabled={!_.isEmpty(item.errorFields)}
                >
                  {i18n.getString(_TL_('Save'))}
                </PrimaryButton>

                <DefaultButton
                  className="rsa-editor-cancel-button"
                  onClick={onCancelClicked}
                >
                  {i18n.getString(_TL_('Cancel'))}
                </DefaultButton>
              </Stack>
            }
          </Stack>
        </CopilotRepositoryProvider>
      </FluentProvider>
    );
  };

  return (
    <ErrorContext.Provider value={errorContextValue}>
      <PermissionsContext.Provider value={permissions}>
        <Stack
          horizontal
          className="rsa-content-container"
          styles={containerStyles.container}
        >
          {renderLeftColumnWithNewCopilotUI()}
          <Stack
            className="rsa-right-column"
            styles={containerStyles.preview}
          >
            <div className="right-columns fixed">
              { permissions && permissions.dynamic && !permissions.dynamic.AdStrengthTestingNoPanel &&
              <ResponsiveSearchAdStrength
                adItem={data}
                adGroupId={adGroupId}
                campaignId={campaignId}
                onFetchAdStrength={onFetchAdStrengthV2}
                i18n={i18n}
                isForVnext={isForVnext}
                logAdStrength={logPrefilledAdStrengthCallback}
              />
           }
              <div style={stickyAdPreviewStyles}>
                {renderImageExtension ?
                  <AdPreviewComponent
                    key={cachedAd.key || 'dummy'}
                    data={cachedAd.data || {}}
                    i18n={i18n}
                    disclaimerInfo={disclaimerInfo}
                    image={previewImage}
                    permissions={permissions}
                  />
                :
                  <AdPreviewComponent
                    key={cachedAd.key || 'dummy'}
                    data={cachedAd.data || {}}
                    i18n={i18n}
                    disclaimerInfo={disclaimerInfo}
                  />
                }
              </div>
            </div>
          </Stack>
        </Stack>
      </PermissionsContext.Provider>
    </ErrorContext.Provider>
  );
}

ResponsiveSearchAdEditorBase.propTypes = {

  item: PropTypes.shape({
    data: PropTypes.shape({
      Headlines: PropTypes.arrayOf(PropTypes.shape({})),
      Descriptions: PropTypes.arrayOf(PropTypes.shape({})),
    }).isRequired,
    errorFields: PropTypes.arrayOf(PropTypes.string),
  }),

  errors: PropTypes.arrayOf(PropTypes.shape({
    Property: PropTypes.string,
    Code: PropTypes.string,
    Message: PropTypes.string.isRequired,
  })),

  i18n: PropTypes.shape({
    getLocale: PropTypes.func.isRequired,
    getString: PropTypes.func.isRequired,
  }).isRequired,

  isNewTextAssetsButtonStyle: PropTypes.bool,

  onSave: PropTypes.func,

  onCancel: PropTypes.func,

  onChanged: PropTypes.func,

  onFetchAdStrengthV2: PropTypes.func,

  onFetchDisclaimerInfo: PropTypes.func,

  showActionBar: PropTypes.bool,

  showInitialErrors: PropTypes.bool,

  timeZone: PropTypes.number,

  AdPreviewComponent: PropTypes.elementType,

  permissions: PropTypes.shape({
    dynamic: PropTypes.shape({
      AdStrengthTestingNoPanel: PropTypes.bool,
      AdStrengthTestingNoPrefill: PropTypes.bool,
      CopilotDKIRecommendation: PropTypes.bool,
    }),
    IsVanityUrlEnabled: PropTypes.bool,
  }),

  urlOptionsElement: PropTypes.shape({}),

  token: PropTypes.string,

  triggerTestUurl: PropTypes.func,

  showUurlTestTool: PropTypes.bool,

  showFinalUrlSuffix: PropTypes.bool,

  showAdCustomizers: PropTypes.bool,

  showDynamicTextExtention: PropTypes.bool,

  isForVnext: PropTypes.bool,

  updateSuggestionMeasurement: PropTypes.func,

  edmCampaign: PropTypes.shape({}),

  jsDataCampaign: PropTypes.shape({}),

  renderImageExtension: PropTypes.func,

  injectSetPreviewImage: PropTypes.func,

  perfMarker: PropTypes.shape({
    createChild: PropTypes.func,
  }),

  serverContext: PropTypes.shape({}),
  scope: PropTypes.shape({
    campaignId: PropTypes.number,
    adGroupId: PropTypes.number,
  }),
  editMode: PropTypes.bool,
  saveLogAppliedSuggestionsFunc: PropTypes.func,
  isSaving: PropTypes.bool,
  isCanceling: PropTypes.bool,
  onFirstErrorHandler: PropTypes.func,
  saveAssetLibraryEventHandler: PropTypes.func,
  activityLogger: PropTypes.shape({}),
};

ResponsiveSearchAdEditorBase.defaultProps = {
  showActionBar: true,
  showInitialErrors: true,
  item: { data: {} },
  errors: [],
  isNewTextAssetsButtonStyle: false,
  onSave: _.noop,
  onCancel: _.noop,
  onChanged: _.noop,
  onFetchAdStrengthV2: _.noop,
  onFetchDisclaimerInfo: null,
  timeZone: null,
  AdPreviewComponent: ResponsiveSearchAdPreviewUAPL,
  urlOptionsElement: null,
  token: '',
  triggerTestUurl: _.noop,
  showUurlTestTool: false,
  showFinalUrlSuffix: false,
  showAdCustomizers: false,
  showDynamicTextExtention: false,
  isForVnext: false,
  updateSuggestionMeasurement: _.noop,
  edmCampaign: {},
  jsDataCampaign: {},
  renderImageExtension: undefined,
  injectSetPreviewImage: _.noop,
  perfMarker: {},
  serverContext: {},
  scope: {},
  editMode: false,
  saveLogAppliedSuggestionsFunc: _.noop,
  saveAssetLibraryEventHandler: _.noop,
  isSaving: false,
  isCanceling: false,
  onFirstErrorHandler: _.noop,
  permissions: {
    dynamic: {
      AdStrengthTestingNoPanel: false,
      AdStrengthTestingNoPrefill: false,
      CopilotDKIRecommendation: false,
    },
    IsVanityUrlEnabled: false,
  },
  activityLogger: undefined,
};

export const ResponsiveSearchAdEditor = _.compose(
  withActivityContext,
  withBannerService,
  withRecommendationContext
)(ResponsiveSearchAdEditorBase);
